import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { LogOut, LayoutGrid, Plus, Users, CreditCard, ShieldCheck, Zap } from "lucide-react";

function UsagePill() {
  const { sub } = useSubscription();
  if (!sub) return null;
  if (sub.is_admin) {
    return (
      <span
        data-testid="nav-admin-unlimited"
        className="hidden md:inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 bg-black text-[#BFDBFE] border border-black"
        title="Admin account · unlimited generation"
      >
        <ShieldCheck className="w-3 h-3" /> Admin · Unlimited
      </span>
    );
  }
  const trials = sub.free_trials_remaining || 0;
  if (!sub.subscribed || !sub.quota) {
    if (trials > 0) {
      return (
        <Link
          data-testid="nav-trial"
          to="/app/billing"
          className="hidden md:inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 bg-white text-black border border-black hover:bg-[#DBEAFE] transition-colors"
          title="You have a free trial note. Click to see the plan."
        >
          <Zap className="w-3 h-3" /> Free trial · 1 note
        </Link>
      );
    }
    return (
      <Link
        data-testid="nav-subscribe"
        to="/app/billing"
        className="hidden md:inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 bg-[#DBEAFE] text-black border border-black/20 hover:bg-[#BFDBFE] transition-colors"
        title="Subscribe to start generating notes"
      >
        <CreditCard className="w-3 h-3" /> Subscribe
      </Link>
    );
  }
  const q = sub.quota;
  const includedUsed = q.included_used;
  const includedTotal = q.included_total;
  const extra = q.extra_remaining;
  const low = q.low_warning;
  const dead = q.exhausted;
  return (
    <Link
      data-testid="nav-usage"
      to="/app/billing"
      className={`hidden md:inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors ${
        dead
          ? "bg-red-100 text-red-900 border-red-300 hover:bg-red-200"
          : low
          ? "bg-amber-100 text-amber-900 border-amber-300 hover:bg-amber-200"
          : "bg-white text-black border-black/20 hover:bg-black/5"
      }`}
      title={dead ? "Quota exhausted — buy credits" : "View plan & usage"}
    >
      <span>
        Videos: <span className="font-bold">{includedUsed}</span> / {includedTotal}
      </span>
      {extra > 0 && (
        <span className="pl-2 border-l border-current/30 opacity-80">+{extra} extra</span>
      )}
    </Link>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { sub } = useSubscription();
  const nav = useNavigate();
  const isAuthed = user && typeof user === "object";
  // Backend is the source of truth for admin status (respects ADMIN_EMAIL env).
  // We keep the local email fallback so the link appears immediately after login,
  // before /subscription/me resolves.
  const adminEmail = (process.env.REACT_APP_ADMIN_EMAIL || "admin@smartnotes.ai").toLowerCase();
  const isAdmin =
    !!sub?.is_admin ||
    (isAuthed && (user.email || "").toLowerCase() === adminEmail);

  // First-name for the greeting chip. Falls back to email local-part.
  const rawName = (user?.name || "").trim();
  const firstName = rawName
    ? rawName.split(/\s+/)[0]
    : (user?.email || "").split("@")[0];
  const initial = (firstName || "?").charAt(0).toUpperCase();

  return (
    <header
      data-testid="app-navbar"
      className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-black/10"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
        <Link
          data-testid="nav-logo"
          to={isAuthed ? "/app" : "/"}
          className="flex items-center gap-2 font-heading font-black text-xl tracking-tighter"
        >
          <span className="inline-block w-6 h-6 bg-[#3B82F6] border border-black" />
          Smart <span className="text-[#3B82F6]">S</span> Notes
        </Link>

        <nav className="flex items-center gap-2 sm:gap-3">
          {isAuthed ? (
            <>
              <span
                data-testid="nav-greeting"
                className="hidden md:inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 bg-black text-white"
                title={user?.email || ""}
              >
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#3B82F6] text-white text-[10px] font-heading font-black">
                  {initial}
                </span>
                Hi, <span className="font-heading font-bold normal-case tracking-normal">{firstName}</span>
              </span>
              <UsagePill />
              <Link
                data-testid="nav-dashboard"
                to="/app"
                className="hidden sm:inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-3 py-2 hover:bg-black/5 transition-colors"
              >
                <LayoutGrid className="w-3.5 h-3.5" /> Library
              </Link>
              <Link
                data-testid="nav-teams"
                to="/app/teams"
                className="hidden sm:inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-3 py-2 hover:bg-black/5 transition-colors"
              >
                <Users className="w-3.5 h-3.5" /> Teams
              </Link>
              <Link
                data-testid="nav-billing"
                to="/app/billing"
                className="hidden sm:inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-3 py-2 hover:bg-black/5 transition-colors"
              >
                <CreditCard className="w-3.5 h-3.5" /> Billing
              </Link>
              {isAdmin && (
                <Link
                  data-testid="nav-admin"
                  to="/app/admin"
                  className="hidden sm:inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-3 py-2 hover:bg-black/5 transition-colors"
                >
                  <ShieldCheck className="w-3.5 h-3.5" /> Admin
                </Link>
              )}
              <Link
                data-testid="nav-new-note"
                to="/app/new"
                className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-2 bg-black text-white btn-brutal"
              >
                <Plus className="w-3.5 h-3.5" /> New
              </Link>
              <button
                data-testid="nav-logout"
                onClick={async () => { await logout(); nav("/"); }}
                className="p-2 hover:bg-black/5 transition-colors"
                title="Log out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <Link
                data-testid="nav-login"
                to="/login"
                className="font-mono text-xs uppercase tracking-widest px-3 py-2 hover:bg-black/5 transition-colors"
              >
                Log in
              </Link>
              <Link
                data-testid="nav-signup"
                to="/register"
                className="font-mono text-xs uppercase tracking-widest px-4 py-2 bg-[#3B82F6] text-white btn-brutal"
              >
                Get Started
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
